// src/pages/admin/hr/TransportClaims.jsx
import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, RefreshCw, Calendar, DollarSign, User } from 'lucide-react'
import TransportClaimService from '../../../services/transportClaimService'
import toast from 'react-hot-toast'
import CreateTransportClaimModal from '../../../components/modals/CreateTransportClaimModal'
import EditTransportClaimModal from '../../../components/modals/EditTransportClaimModal'
import DeleteConfirmModal from '../../../components/modals/DeleteConfirmModal'

const TransportClaims = () => {
  const [claims, setClaims] = useState([])
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedClaim, setSelectedClaim] = useState(null)

  useEffect(() => {
    fetchTransportClaims()
  }, [])

  const fetchTransportClaims = async () => {
    try {
      setLoading(true)
      const response = await TransportClaimService.getAllTransportClaims()
      if (response.success && response.data) {
        setClaims(response.data.claims || response.data)
      }
    } catch (error) {
      console.error('Error fetching transport claims:', error)
      toast.error('Failed to load transport claims')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchTransportClaims()
    setRefreshing(false)
    toast.success('Transport claims refreshed')
  }

  const handleCreate = () => {
    setShowCreateModal(true)
  }

  const handleEdit = (claim) => {
    setSelectedClaim(claim)
    setShowEditModal(true)
  }

  const handleDelete = (claim) => {
    setSelectedClaim(claim)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    try {
      const response = await TransportClaimService.deleteTransportClaim(selectedClaim._id)
      // Check for success or 204 No Content status (successful deletion)
      if (response.success || response.status === 204 || !response) {
        toast.success('Transport claim deleted successfully')
        fetchTransportClaims()
        setShowDeleteModal(false)
        setSelectedClaim(null)
      }
    } catch (error) {
      console.error('Error deleting transport claim:', error)
      toast.error(error.response?.data?.message || 'Failed to delete transport claim')
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Transport Claims</h1>

          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add Transport Claim</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Branch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Note
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="h-6 w-6 text-gray-400 animate-spin" />
                      <span className="ml-2 text-gray-500">Loading transport claims...</span>
                    </div>
                  </td>
                </tr>
              ) : claims.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No transport claims found. Click "Add Transport Claim" to create one.
                  </td>
                </tr>
              ) : (
                claims.map((claim) => (
                  <tr key={claim._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{formatDate(claim.date)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {claim.employee?.fullName || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {claim.employee?.contactNumber || ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{claim.branch?.name || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm font-semibold text-gray-900">
                          {formatCurrency(claim.amount)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {claim.note || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        <button
                          onClick={() => handleEdit(claim)}
                          className="text-gray-600 hover:text-gray-900 transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(claim)}
                          className="text-gray-600 hover:text-gray-900 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <CreateTransportClaimModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false)
          fetchTransportClaims()
        }}
      />

      <EditTransportClaimModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedClaim(null)
        }}
        claim={selectedClaim}
        onSuccess={() => {
          setShowEditModal(false)
          setSelectedClaim(null)
          fetchTransportClaims()
        }}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedClaim(null)
        }}
        onConfirm={confirmDelete}
        title="Delete Transport Claim"
        message={`Are you sure you want to delete this transport claim for ${selectedClaim?.employee?.fullName}?`}
      />
    </div>
  )
}

export default TransportClaims
